unit Bcl.Json.Attributes;

{$I Bcl.inc}

interface

uses
  Generics.Collections,
  {$IFNDEF PAS2JS}
  Bcl.JSON.Converters,
  {$ENDIF}
  Classes;

type
  {$IFDEF PAS2JS}
  TInclusionMode = (Always, NonDefault);
  TJsonNamingStrategy = class
  end;
  TJsonNamingStrategyClass = class of TJsonNamingStrategy;
  TCustomJsonConverter = class
  end;
  TCustomJsonConverterClass = class of TCustomJsonConverter;
  {$ELSE}
  TInclusionMode = Bcl.Json.Converters.TInclusionMode;
  {$ENDIF}


  /// <summary>Specifies a custom JSON property name for a field or property.</summary>
  /// <remarks>When applied without a name, the member is included in serialization with its default name.</remarks>
  JsonPropertyAttribute = class(TCustomAttribute)
  strict private
    FName: string;
  public
    constructor Create; overload;
    /// <summary>Creates the attribute with a custom JSON property name.</summary>
    /// <param name="AName">The JSON property name to use.</param>
    constructor Create(const AName: string); overload;
    /// <summary>Gets the custom JSON property name, or empty string for the default name.</summary>
    property Name: string read FName;
  end;

  /// <summary>Excludes a field or property from JSON serialization and deserialization.</summary>
  JsonIgnoreAttribute = class(TCustomAttribute)
  end;

  /// <summary>Controls when a property is included in JSON serialization.</summary>
  JsonIncludeAttribute = class(TCustomAttribute)
  strict private
    FInclusionMode: TInclusionMode;
  public
    /// <summary>Creates the attribute with the specified inclusion mode.</summary>
    /// <param name="AInclusionMode">The mode that determines when the property is included.</param>
    constructor Create(AInclusionMode: TInclusionMode);
    /// <summary>Gets the inclusion mode.</summary>
    property InclusionMode: TInclusionMode read FInclusionMode;
  end;

  /// <summary>Specifies a naming strategy for converting property names during JSON serialization.</summary>
  JsonNamingStrategyAttribute = class(TCustomAttribute)
  strict private
    FStrategyClass: TJsonNamingStrategyClass;
  public
    /// <summary>Creates the attribute with the specified naming strategy class.</summary>
    /// <param name="AStrategyClass">The naming strategy class to use.</param>
    constructor Create(AStrategyClass: TJsonNamingStrategyClass);
    /// <summary>Gets the naming strategy class.</summary>
    property StrategyClass: TJsonNamingStrategyClass read FStrategyClass;
  end;

  /// <summary>Specifies custom string values for enumeration members during JSON serialization.</summary>
  JsonEnumValuesAttribute = class(TCustomAttribute)
  strict private
    FEnumValues: TList<string>;
  public
    /// <summary>Creates the attribute with a comma- or semicolon-separated list of enum value names.</summary>
    /// <param name="AEnumValues">The enum value names, separated by commas or semicolons.</param>
    constructor Create(const AEnumValues: string);
    destructor Destroy; override;
    /// <summary>Gets the list of custom enum value names.</summary>
    property EnumValues: TList<string> read FEnumValues;
  end;

  /// <summary>Specifies a custom JSON converter class for a field, property, or type.</summary>
  JsonConverterAttribute = class(TCustomAttribute)
  strict private
    FConverterClass: TCustomJsonConverterClass;
  private
    procedure SetConverterClass(const Value: TCustomJsonConverterClass);
  public
    /// <summary>Creates the attribute with the specified converter class.</summary>
    /// <param name="AConverterClass">The custom JSON converter class to use.</param>
    constructor Create(AConverterClass: TCustomJsonConverterClass);
    /// <summary>Gets or sets the custom JSON converter class.</summary>
    property ConverterClass: TCustomJsonConverterClass read FConverterClass write SetConverterClass;
  end;

  /// <summary>Indicates the object lifetime is managed by the class itself, so serializer should not destroy it.</summary>
  /// <remarks>
  /// You can use this attribute in class fields that are objects, or in the class declaration itself.
  /// When applied to a class, it means the attribute will be applied to all object fields of that class.
  /// This attribute indicates the serializer that it should not destroy those objects, as their lifetime is managed by the class itself. 
  /// This is useful for classes that implement their own memory management of its associated objects.
  /// </remarks>
  JsonManagedAttribute = class(TCustomAttribute)
  end;

implementation

{ JsonPropertyAttribute }

constructor JsonPropertyAttribute.Create(const AName: string);
begin
  FName := AName;
end;

constructor JsonPropertyAttribute.Create;
begin
  Create('');
end;

{ JsonIncludeAttribute }

constructor JsonIncludeAttribute.Create(AInclusionMode: TInclusionMode);
begin
  FInclusionMode := AInclusionMode;
end;

{ JsonNamingStrategyAttribute }

constructor JsonNamingStrategyAttribute.Create(AStrategyClass: TJsonNamingStrategyClass);
begin
  FStrategyClass := AStrategyClass;
end;

{ JsonEnumValuesAttribute }

constructor JsonEnumValuesAttribute.Create(const AEnumValues: string);
var
  List: TStringList;
  I: Integer;
begin
  FEnumValues := TList<string>.Create;

  List := TStringList.Create;
  try
    {$IFNDEF PAS2JS}
    ExtractStrings([',', ';'], [' '], PChar(AEnumValues), List);
    {$ENDIF}
    for I := 0 to List.Count - 1 do
      FEnumValues.Add(List[I]);
  finally
    List.Free;
  end;
end;

destructor JsonEnumValuesAttribute.Destroy;
begin
  FEnumValues.Free;
  inherited;
end;

{ JsonConverterAttribute }

constructor JsonConverterAttribute.Create(
  AConverterClass: TCustomJsonConverterClass);
begin
  FConverterClass := AConverterClass;
end;

procedure JsonConverterAttribute.SetConverterClass(
  const Value: TCustomJsonConverterClass);
begin
  FConverterClass := Value;  
end;

end.
